import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@phosphor-icons/react/dist/csr/Check';
import { PaperPlaneTiltIcon } from '@phosphor-icons/react/dist/csr/PaperPlaneTilt';
import { PhoneIcon } from '@phosphor-icons/react/dist/csr/Phone';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';

import { LogCallDialog } from './LogCallDialog';
import { QUEUE_EMPTY_KEY, QUEUE_LABEL_KEY, REQUEST_STATUS_LABEL_KEY } from './requestLabels';
import { PageActions } from '../../app/shell/PageActions';
import { FilterBar } from '../../design-system/composites/FilterBar/FilterBar';
import { Button } from '../../design-system/primitives/Button/Button';
import {
  DataTable,
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { Input } from '../../design-system/primitives/Input/Input';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import { ExportButton } from '../shared/ExportButton';
import { ApiError } from '../../lib/api/client';
import {
  requestsApi,
  type CaseCallRow,
  type CaseRequestQueue,
  type CaseRequestQuery,
  type CaseRequestRow,
} from '../../lib/api/requests';
import { formatTimestamp } from '../../lib/format/dateTime';
import { kindLabel } from './kindLabel';
import styles from './RequestList.module.css';

/**
 * The requests queue (M-09, Task 6, spec §5) — `/requests`, the nav entry
 * that has pointed nowhere since Phase 1. A segmented control over the
 * three queues (spec D-4), the filter row `CaseList.tsx` already
 * established, and a table.
 *
 * ## The chase action comes from `next_action`, never re-derived (Global
 * Constraint, spec §5)
 *
 * `renderChaseAction()` below reads `row.next_action` and switches on its
 * FOUR closed values — it does not look at `row.resend`, `row.calls` or any
 * timestamp to decide whether a button is due. `App\Support\Requests\
 * NextAction::for()` is the one place that rule lives; a second
 * implementation here, however carefully mirrored, is exactly the legacy
 * defect this milestone exists to fix (`nonuploadedrequests.php` deciding
 * what to show, `call.php` deciding what to accept, and the two
 * disagreeing) — see `RequestList.test.tsx`'s own "next_action" suite for
 * the proof.
 *
 * ## Completing a request is a SEPARATE control, not a fifth `next_action` value
 *
 * `App\Support\Requests\NextAction::for()`'s own four values are
 * `resend`/`first_call`/`second_call`/`none` — completion is never one of
 * them (spec D-4/D-7: the state machine is about CHASING a non-answer, and
 * a request whose answer has already arrived is `next_action: 'none'`
 * regardless, spec D-7's own 2026-08-24 amendment). Without an independent
 * "Complete" control, the Open queue — spec D-4's "asked, answered, not
 * yet dealt with" — would be a dead end nothing in this screen could ever
 * clear. `renderComplete()` is therefore gated on its OWN permission
 * (`request.complete`) and the row's OWN `status`, never on `next_action`.
 *
 * ## Logging a call refreshes ONE row, not the whole page
 *
 * `POST /case-requests/{request}/calls` answers a standalone `CaseCallRow`
 * — it does not carry the owning request's updated `calls`/`next_action`.
 * `handleLogged` re-reads that one row with `requestsApi.get()` and splices
 * it back into `rows` — the identical "the API already answered what a
 * refetch would ask again" shape `CaseMessages.tsx` documents, extended by
 * one more read because this particular write does not return the shape
 * this list needs.
 */

export interface RequestListProps {
  permissions: readonly string[];
  /**
   * Open one request's own detail. OPTIONAL for the same reason
   * `CaseRequests`' identical prop is: a caller with nowhere to mount a
   * detail surface still gets a working queue, and no row pretends to be
   * clickable when there is nothing to click through to.
   */
  onOpen?: (uuid: string) => void;
  /**
   * Whether this list is the view on screen. The screen keeps the list
   * mounted (hidden) under an opened record, and `PageActions` portals into
   * the shell's title row whatever `hidden` says — so a hidden list withdraws
   * its page commands, or they would sit beside the detail.
   */
  active?: boolean;
}

interface RequestListRow extends Row, CaseRequestRow {
  /**
   * FLATTENED off `case.case_number`, and load-bearing rather than a
   * convenience.
   *
   * `DataTable` makes the leading cell the row's opener only when that
   * column has NO custom `render` and the row object itself carries a
   * non-empty value under the column's key (see `leadingCell()`). This
   * column had both problems — a `render` that reached into the nested
   * `case` relation, and no `case_number` key on the row at all — so wiring
   * `onRowActivate` without this produced a table nothing could open, with
   * no error anywhere. `CaseList.tsx` flattens for the same reason.
   */
  case_number: string | null;
}

function toRow(node: CaseRequestRow): RequestListRow {
  return { ...node, id: node.uuid, case_number: node.case?.case_number ?? null };
}

const QUEUES: readonly CaseRequestQueue[] = ['open', 'awaiting', 'complete'];

interface FilterState {
  caseNumber: string;
  mobile: string;
  mine: boolean;
}

const BLANK_FILTERS: FilterState = { caseNumber: '', mobile: '', mine: false };

function toQuery(
  queue: CaseRequestQueue,
  filters: FilterState,
  cursor?: string,
  perPage?: number,
): CaseRequestQuery {
  const query: CaseRequestQuery = { queue };
  if (filters.caseNumber.trim() !== '') query.case_number = filters.caseNumber.trim();
  if (filters.mobile.trim() !== '') query.mobile = filters.mobile.trim();
  if (filters.mine) query.mine = true;
  if (cursor !== undefined) query.cursor = cursor;
  if (perPage !== undefined) query.per_page = perPage;
  return query;
}

/**
 * Read off the OUTGOING QUERY, exactly as `CaseList.tsx`'s own
 * `hasAppliedFilters` is — never off `appliedFilters` state from the
 * closure `load` was created in. `queue` is excluded: it always travels
 * (the segmented control has no "unset" state), so its presence would make
 * every load count as filtered.
 */
function hasAppliedFilters(query: CaseRequestQuery): boolean {
  return query.case_number !== undefined || query.mobile !== undefined || query.mine !== undefined;
}

export function RequestList({ permissions, onOpen, active = true }: RequestListProps) {
  const { t, i18n } = useTranslation();
  const canResend = permissions.includes('sms.send');
  const canCall = permissions.includes('request.call');
  const canComplete = permissions.includes('request.complete');

  const [queue, setQueue] = useState<CaseRequestQueue>('open');
  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly RequestListRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  // 50 — `CaseRequestIndexRequest::DEFAULT_PER_PAGE` server-side.
  const pages = useCursorPages();
  // Destructured — `pages` is a fresh object every render; see
  // `CaseList.tsx`'s identical note.
  const { observe } = pages;
  const [draftFilters, setDraftFilters] = useState<FilterState>(BLANK_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(BLANK_FILTERS);

  const [busyUuid, setBusyUuid] = useState<string | null>(null);

  /*
   * A refused row action has to say so. Both handlers below used to catch and
   * discard, on the reasoning that the row simply keeps the state the last
   * successful read reported — which is true of the DATA and useless to the
   * person who clicked. A 409 ("already resent", "already complete") produced
   * no feedback of any kind: the button flickered and nothing changed.
   */
  const [actionFailed, setActionFailed] = useState(false);
  const [callDialogUuid, setCallDialogUuid] = useState<string | null>(null);

  const requestSeq = useRef(0);

  const load = useCallback(async (query: CaseRequestQuery) => {
    const seq = ++requestSeq.current;
    setStatus('loading');
    setReference(null);

    try {
      const page = await requestsApi.list(query);
      if (seq !== requestSeq.current) return;
      setRows(page.data.map(toRow));
      setTotal(page.meta.total);
      observe({ next_cursor: page.meta.next_cursor, total: page.meta.total });
      setStatus(page.data.length === 0 ? (hasAppliedFilters(query) ? 'no-results' : 'empty') : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (seq !== requestSeq.current) return;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [observe]);

  useEffect(() => {
    void load(toQuery(queue, appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }, [load, queue, appliedFilters, pages.cursor, pages.perPage]);

  function handleQueueChange(value: string): void {
    setQueue(value as CaseRequestQueue);
    pages.first();
  }

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    pages.first();
    setAppliedFilters(draftFilters);
  }

  function handleClearFilters(): void {
    setDraftFilters(BLANK_FILTERS);
    pages.first();
    setAppliedFilters(BLANK_FILTERS);
  }

  function retry(): void {
    void load(toQuery(queue, appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }

  function updateDraft(field: 'caseNumber' | 'mobile') {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setDraftFilters((current) => ({ ...current, [field]: value }));
    };
  }

  function replaceRow(updated: CaseRequestRow): void {
    setRows((current) => current.map((row) => (row.uuid === updated.uuid ? toRow(updated) : row)));
  }

  async function handleResend(uuid: string): Promise<void> {
    if (busyUuid !== null) return;
    setBusyUuid(uuid);
    setActionFailed(false);
    try {
      const updated = await requestsApi.resend(uuid);
      replaceRow(updated);
    } catch {
      setActionFailed(true);
    } finally {
      setBusyUuid(null);
    }
  }

  async function handleComplete(uuid: string): Promise<void> {
    if (busyUuid !== null) return;
    setBusyUuid(uuid);
    setActionFailed(false);
    try {
      const updated = await requestsApi.complete(uuid);
      replaceRow(updated);
    } catch {
      setActionFailed(true);
    } finally {
      setBusyUuid(null);
    }
  }

  async function handleLogged(_call: CaseCallRow): Promise<void> {
    const uuid = callDialogUuid;
    setCallDialogUuid(null);
    if (uuid === null) return;

    try {
      // `POST .../calls` answers a standalone `CaseCallRow` (see this
      // module's own docblock) — the owning request's `next_action`/`calls`
      // need a fresh read, not a guess built from `call` alone.
      const updated = await requestsApi.get(uuid);
      replaceRow(updated);
    } catch {
      // The dialog already closed on the call itself succeeding
      // (`call` above is proof of that); a failed follow-up read leaves
      // this one row stale until the next full list load, which is the
      // same "not currently shown as failed" choice `handleResend` makes.
    }
  }

  function renderMobile(row: Row): ReactNode {
    return (row as unknown as CaseRequestRow).mobile;
  }

  function renderKind(row: Row): ReactNode {
    return kindLabel((row as unknown as CaseRequestRow).kind, t) ?? t('table.notRecorded');
  }

  function renderStatus(row: Row): ReactNode {
    return t(REQUEST_STATUS_LABEL_KEY[(row as unknown as CaseRequestRow).status]);
  }

  function renderAskedAt(row: Row): ReactNode {
    const value = (row as unknown as CaseRequestRow).asked_at;
    return value === null ? null : formatTimestamp(value, i18n.language);
  }

  function renderUploadedAt(row: Row): ReactNode {
    const value = (row as unknown as CaseRequestRow).uploaded_at;
    return value === null ? null : formatTimestamp(value, i18n.language);
  }

  /**
   * THE ONE PLACE `next_action` DECIDES WHAT RENDERS. Four closed values,
   * four branches, nothing else read off the row to make the decision —
   * see this file's own docblock.
   */
  function renderChaseAction(row: Row): ReactNode {
    const record = row as unknown as CaseRequestRow;
    const busy = busyUuid === record.uuid;

    switch (record.next_action) {
      case 'resend':
        return canResend ? (
          <Button
            variant="secondary"
            loading={busy}
            onClick={() => {
              void handleResend(record.uuid);
            }}
            icon={<PaperPlaneTiltIcon size={16} />}
          >
            {t('requests.resend')}
          </Button>
        ) : null;
      case 'first_call':
        return canCall ? (
          <Button
            variant="secondary"
            onClick={() => {
              setCallDialogUuid(record.uuid);
            }}
            icon={<PhoneIcon size={16} />}
          >
            {t('requests.logFirstCall')}
          </Button>
        ) : null;
      case 'second_call':
        return canCall ? (
          <Button
            variant="secondary"
            onClick={() => {
              setCallDialogUuid(record.uuid);
            }}
            icon={<PhoneIcon size={16} />}
          >
            {t('requests.logSecondCall')}
          </Button>
        ) : null;
      case 'none':
        return null;
    }
  }

  function renderComplete(row: Row): ReactNode {
    const record = row as unknown as CaseRequestRow;
    if (!canComplete || record.status !== 'open') return null;
    const busy = busyUuid === record.uuid;

    return (
      <Button
        variant="ghost"
        loading={busy}
        onClick={() => {
          void handleComplete(record.uuid);
        }}
        icon={<CheckIcon size={16} />}
      >
        {t('requests.complete')}
      </Button>
    );
  }

  function renderActions(row: Row): ReactNode {
    const chase = renderChaseAction(row);
    const complete = renderComplete(row);
    if (chase === null && complete === null) return null;

    return (
      <div className={styles['rowActions']}>
        {chase}
        {complete}
      </div>
    );
  }

  const queueOptions = QUEUES.map((value) => ({ value, label: t(QUEUE_LABEL_KEY[value]) }));

  const columns: readonly Column[] = [
    { key: 'case_number', label: t('table.caseNumber'), neverMirrors: true },
    { key: 'mobile', label: t('table.mobile'), neverMirrors: true, render: renderMobile },
    { key: 'kind', label: t('requests.kind'), neverMirrors: true, render: renderKind },
    { key: 'status', label: t('table.status'), render: renderStatus },
    { key: 'asked_at', label: t('requests.askedAt'), render: renderAskedAt },
    { key: 'uploaded_at', label: t('requests.uploadedAt'), render: renderUploadedAt },
    { key: 'actions', label: t('table.actions'), render: renderActions },
  ];

  return (
    <div className={styles['page']}>
      {/* No second heading under the shell's `<h1>`: Export sits in the title
          row. The SELECTED QUEUE travels with its filters — `toQuery(queue,
          ...)` is exactly the call the fetch makes, minus the cursor, so the
          file is the queue the user is looking at. `canExport` is
          unconditional — reaching this screen already implies request read
          access. */}
      {active ? (
        <PageActions>
          <ExportButton
            module="case-requests"
            filters={{ ...toQuery(queue, appliedFilters) }}
            canExport
          />
        </PageActions>
      ) : null}

      <FilterBar
        onSubmit={handleFilterSubmit}
        onClear={handleClearFilters}
        leading={
          <SegmentedControl
            label={t('requests.queueLabel')}
            options={queueOptions}
            value={queue}
            onChange={handleQueueChange}
          />
        }
      >
        <Input
          label={t('table.caseNumber')}
          width="compact"
          dir="ltr"
          value={draftFilters.caseNumber}
          onChange={updateDraft('caseNumber')}
        />
        <Input
          label={t('table.mobile')}
          width="phone"
          dir="ltr"
          value={draftFilters.mobile}
          onChange={updateDraft('mobile')}
        />

        <label className={styles['mineOption']}>
          <input
            type="checkbox"
            checked={draftFilters.mine}
            onChange={(event) => {
              const { checked } = event.target;
              setDraftFilters((current) => ({ ...current, mine: checked }));
            }}
          />
          {t('requests.showMine')}
        </label>
      </FilterBar>

      {/*
        Mounted EMPTY from the first frame rather than inserted on failure, the
        same shape every alert region in this codebase takes: a `role="alert"`
        added to the DOM at the moment it fills is announced inconsistently.
      */}
      <div className={styles['actionFailure']} role="alert" aria-atomic="true">
        {actionFailed ? t('requests.actionFailed') : null}
      </div>

      <DataTable
        caption={t('requests.tableCaption')}
        columns={columns}
        rows={rows}
        status={status}
        empty={{ body: t(QUEUE_EMPTY_KEY[queue]) }}
        noResults={{ title: t('list.noResultsTitle') }}
        onClearFilters={handleClearFilters}
        error={{ title: t('requests.queueLoadFailed') }}
        {...(onOpen === undefined
          ? {}
          : {
              onRowActivate: (row: Row) => {
                onOpen(row.id);
              },
            })}
      />

      <Pagination pages={pages} total={total} rowsOnPage={rows.length} />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button variant="secondary" onClick={retry}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('actions.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="requestListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <LogCallDialog
        open={callDialogUuid !== null}
        requestUuid={callDialogUuid}
        onClose={() => {
          setCallDialogUuid(null);
        }}
        onLogged={(call) => {
          void handleLogged(call);
        }}
      />
    </div>
  );
}
